/**
 * What has to hold for a `layout` fragment to be a usable email layout.
 *
 * A layout is not a piece of an email — it **is** the email document. The server
 * prepends `{% extends '<layout_key>' %}` to a template's body, so everything the
 * template emits lands in one of the layout's `{% block %}`s and nothing else in
 * the layout is reachable from the outside. Two consequences, and both of them
 * fail silently:
 *
 * - **A layout with no content block discards the email.** Twig does not error on
 *   content emitted into a block the parent never declared; it renders the layout
 *   and drops the body. `<p>test</p>` saved as a layout is a valid Twig template
 *   and a working way to send every customer an email that says "test".
 * - **A layout with broken markup breaks every email that uses it.** It wraps the
 *   whole document, so an unclosed `<td>` is not a local glitch — it is Gmail
 *   deciding on its own where the table ends, in every send.
 *
 * Neither shows up in the drawer's live preview: twig.js renders the layout
 * happily, and the browser silently repairs the markup exactly the way a mail
 * client would not.
 *
 * ### Why the checks are lenient in two specific places
 *
 * Email HTML is written against Outlook, and two of its idioms look like broken
 * markup to any balance check:
 *
 * - **MSO conditional comments** (`<!--[if mso]><table><tr><td><![endif]-->`)
 *   deliberately open tags they never close, and close tags they never opened.
 *   They are HTML comments, so they are skipped whole.
 * - **Tags opened inside `{% if %}` / `{% for %}`** may legitimately be closed in
 *   another branch or another iteration. Those are reported as warnings rather
 *   than errors: the check cannot know which branch renders, and a save must not
 *   be blocked by a limitation of the checker.
 */
import { layoutBlocks } from './fragmentPreview';

export const LAYOUT_PROBLEM = {
  /** No `{% block %}` the email's content could land in. */
  NO_CONTENT_SLOT: 'no-content-slot',
  /** Has content blocks, but none named the one the visual editor fills. */
  UNCONVENTIONAL_SLOT: 'unconventional-slot',
  /** Not an HTML document: no `<html>` / `<body>`, and it extends nothing. */
  NOT_A_DOCUMENT: 'not-a-document',
  /** A document without a doctype — quirks mode in several clients. */
  MISSING_DOCTYPE: 'missing-doctype',
  /** An element is still open at the end of the source. */
  UNCLOSED_TAG: 'unclosed-tag',
  /** A closing tag with nothing open to close. */
  STRAY_CLOSING_TAG: 'stray-closing-tag',
  /** `</b>` closing while `<i>` is still open inside it. */
  MISNESTED_TAG: 'misnested-tag',
  /** A `{% if %}` / `{% for %}` / `{% block %}` with no matching `end…`. */
  UNCLOSED_TWIG_TAG: 'unclosed-twig-tag',
  /** An `{% end… %}` matching nothing that is open. */
  STRAY_TWIG_END: 'stray-twig-end',
} as const;

export type LayoutProblemSeverity = 'error' | 'warning';

export interface LayoutProblem {
  code: string;
  severity: LayoutProblemSeverity;
  /**
   * Technical, English, and deliberately specific — it names the tag and the
   * line. The UI shows a translated sentence for `code` and keeps this as the
   * secondary detail, the same split `issueMessages.ts` uses for preflight.
   */
  detail: string;
  /** Offset in the source, so the editor can reveal the offending markup. */
  at?: number;
}

/**
 * The block name a visually-built email compiles into (`DEFAULT_LAYOUT_SLOT` in
 * `visual/document.ts`), plus the name hand-written layouts have always used.
 * Duplicated rather than imported: the visual editor already imports from this
 * folder, and one constant is not worth a cycle between the two.
 */
const CONVENTIONAL_SLOTS = ['content', 'body'];

/**
 * Blocks that are not content regions: one is the `<title>`, the other the
 * hidden inbox preview line. A layout declaring only these has nowhere for the
 * email to go — the same exclusion `buildFragmentPreviewSource` makes when it
 * marks the regions of a layout preview.
 */
const CHROME_BLOCKS = new Set(['subject', 'preheader', 'title']);

/** Never closed, so they must not open a nesting level. */
const VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

/**
 * Elements HTML lets you leave unclosed — `<td>foo<td>bar` is well-formed HTML.
 * Table soup is written this way often enough that flagging it would report
 * "broken" for markup every mail client parses as intended, so an unclosed one
 * is not a problem and a closing tag may pop past it.
 */
const OPTIONAL_END_TAGS = new Set([
  'p',
  'li',
  'td',
  'th',
  'tr',
  'tbody',
  'thead',
  'tfoot',
  'option',
  'optgroup',
  'dt',
  'dd',
  'caption',
  'colgroup',
]);

/** Their content is not markup; matched whole so it is never scanned. */
const OPAQUE_TAGS = ['pre', 'textarea', 'script', 'style'];

/** Twig tags that open a scope and need an `end…`. */
const TWIG_OPENERS = new Set([
  'if',
  'for',
  'block',
  'embed',
  'macro',
  'apply',
  'autoescape',
  'filter',
  'spaceless',
  'verbatim',
  'sandbox',
  'with',
  'trans',
  'cache',
  'set',
]);

/** Scopes whose body may or may not render, which is what makes tags in them unverifiable. */
const TWIG_CONDITIONAL_SCOPES = new Set(['if', 'for']);

const TWIG_PATTERN = /\{#[\s\S]*?#\}|\{%-?[\s\S]*?-?%\}|\{\{[\s\S]*?\}\}/g;

const HTML_PATTERN = new RegExp(
  [
    // Opaque elements, content included.
    `<(?:${OPAQUE_TAGS.join('|')})\\b[^>]*>[\\s\\S]*?</(?:${OPAQUE_TAGS.join(
      '|',
    )})\\s*>`,
    '<!--[\\s\\S]*?-->', // HTML comment — MSO conditionals are these
    '<!\\[[^\\]]*\\]>', // downlevel-revealed conditional close
    '<!doctype[^>]*>',
    '</?[a-zA-Z][^>]*>',
  ].join('|'),
  'gi',
);

function twigKeyword(tag: string): string {
  return (
    tag
      .replace(/^\{%-?\s*/, '')
      .match(/^[a-zA-Z_]+/)?.[0]
      .toLowerCase() ?? ''
  );
}

function htmlTagName(tag: string): string {
  return (
    tag
      .replace(/^<\/?/, '')
      .match(/^[a-zA-Z0-9]+/)?.[0]
      .toLowerCase() ?? ''
  );
}

/** The 1-based line an offset falls on, for the technical detail. */
function lineAt(source: string, index: number): number {
  let line = 1;
  for (let cursor = 0; cursor < index && cursor < source.length; cursor += 1) {
    if (source[cursor] === '\n') line += 1;
  }
  return line;
}

interface TwigScope {
  keyword: string;
  index: number;
}

interface TwigScan {
  problems: LayoutProblem[];
  /**
   * `{% if %}`/`{% for %}` nesting depth at every offset where it changes, in
   * order. Read by the HTML pass to decide whether a tag sits in a branch.
   */
  conditionalDepthAt: { index: number; depth: number }[];
  /** The source with every Twig construct blanked out, positions preserved. */
  masked: string;
}

/**
 * Walks the Twig constructs: checks that every scope closes, and records where
 * conditional nesting starts and ends.
 *
 * Masking is what makes the HTML pass safe. `{% if a > b %}` inside an attribute
 * value would otherwise end an HTML tag early at that `>`, and the rest of the
 * tag would be scanned as markup — a checker inventing errors in correct source
 * is worse than no checker, because it blocks a save that should go through.
 */
function scanTwig(source: string): TwigScan {
  const problems: LayoutProblem[] = [];
  const conditionalDepthAt: { index: number; depth: number }[] = [];
  const stack: TwigScope[] = [];
  let masked = source;
  let conditionalDepth = 0;

  const noteDepth = (index: number) => {
    conditionalDepthAt.push({ index, depth: conditionalDepth });
  };

  for (const match of source.matchAll(TWIG_PATTERN)) {
    const text = match[0];
    const index = match.index ?? 0;
    masked =
      masked.slice(0, index) +
      // Newlines survive so line numbers stay honest for multi-line tags.
      text.replace(/[^\n]/g, ' ') +
      masked.slice(index + text.length);

    // Comments and output expressions open no scope.
    if (!text.startsWith('{%')) continue;

    const keyword = twigKeyword(text);

    if (keyword === 'set' && text.includes('=')) continue; // inline assignment

    if (keyword.startsWith('end')) {
      const opener = keyword.slice(3);
      const from = [...stack].reverse().findIndex(
        (scope) =>
          // `{% endblock %}` may name its block; `{% end %}`-style shorthands do not
          // exist, so an exact keyword match is all that is needed.
          scope.keyword === opener,
      );
      if (from < 0) {
        problems.push({
          code: LAYOUT_PROBLEM.STRAY_TWIG_END,
          severity: 'error',
          detail: `Line ${lineAt(
            source,
            index,
          )}: {% ${keyword} %} closes nothing`,
          at: index,
        });
        continue;
      }
      // Everything opened after the matched scope was never closed.
      const target = stack.length - 1 - from;
      for (let cursor = stack.length - 1; cursor > target; cursor -= 1) {
        const orphan = stack[cursor];
        problems.push({
          code: LAYOUT_PROBLEM.UNCLOSED_TWIG_TAG,
          severity: 'error',
          detail: `Line ${lineAt(source, orphan.index)}: {% ${
            orphan.keyword
          } %} is not closed before {% ${keyword} %}`,
          at: orphan.index,
        });
      }
      const closed = stack.splice(target);
      for (const scope of closed) {
        if (TWIG_CONDITIONAL_SCOPES.has(scope.keyword)) conditionalDepth -= 1;
      }
      noteDepth(index + text.length);
      continue;
    }

    if (!TWIG_OPENERS.has(keyword)) continue; // include, extends, do, …

    stack.push({ keyword, index });
    if (TWIG_CONDITIONAL_SCOPES.has(keyword)) {
      conditionalDepth += 1;
      noteDepth(index + text.length);
    }
  }

  for (const scope of stack) {
    problems.push({
      code: LAYOUT_PROBLEM.UNCLOSED_TWIG_TAG,
      severity: 'error',
      detail: `Line ${lineAt(source, scope.index)}: {% ${
        scope.keyword
      } %} has no {% end${scope.keyword} %}`,
      at: scope.index,
    });
  }

  return { problems, conditionalDepthAt, masked };
}

interface OpenElement {
  name: string;
  index: number;
  /** Conditional nesting where it was opened — see the leniency note above. */
  conditionalDepth: number;
}

/**
 * Checks that the markup nests. Reports what a browser would have to *repair*,
 * not what a validator would merely frown at: unclosed elements, closing tags
 * that close nothing, and crossed tags.
 */
function scanHtml(
  source: string,
  masked: string,
  conditionalDepthAt: { index: number; depth: number }[],
): LayoutProblem[] {
  const problems: LayoutProblem[] = [];
  const stack: OpenElement[] = [];

  const depthAt = (index: number): number => {
    let depth = 0;
    for (const change of conditionalDepthAt) {
      if (change.index > index) break;
      depth = change.depth;
    }
    return depth;
  };

  /** A tag in a branch cannot be judged, only flagged. */
  const severityFor = (...depths: number[]): LayoutProblemSeverity =>
    depths.some((depth) => depth > 0) ? 'warning' : 'error';

  for (const match of masked.matchAll(HTML_PATTERN)) {
    const text = match[0];
    const index = match.index ?? 0;

    if (
      text.startsWith('<!--') ||
      text.startsWith('<![') ||
      /^<!doctype/i.test(text) ||
      new RegExp(`^<(?:${OPAQUE_TAGS.join('|')})\\b`, 'i').test(text)
    ) {
      continue;
    }

    // `<v:fill>`, `<o:p>` — VML/Office elements, not HTML. They come in pairs
    // inside MSO comments and are not part of the document's structure.
    if (/^<\/?[a-zA-Z0-9]+:/.test(text)) continue;

    const name = htmlTagName(text);
    if (!name) continue;

    if (text.startsWith('</')) {
      const from = [...stack].reverse().findIndex((open) => open.name === name);
      if (from < 0) {
        problems.push({
          code: LAYOUT_PROBLEM.STRAY_CLOSING_TAG,
          severity: severityFor(depthAt(index)),
          detail: `Line ${lineAt(source, index)}: </${name}> closes nothing`,
          at: index,
        });
        continue;
      }
      const target = stack.length - 1 - from;
      for (let cursor = stack.length - 1; cursor > target; cursor -= 1) {
        const orphan = stack[cursor];
        // `<td>foo</tr>` is how half of email HTML is written; the browser and
        // every mail client close the cell for you.
        if (OPTIONAL_END_TAGS.has(orphan.name)) continue;
        problems.push({
          code: LAYOUT_PROBLEM.MISNESTED_TAG,
          severity: severityFor(orphan.conditionalDepth, depthAt(index)),
          detail: `Line ${lineAt(source, orphan.index)}: <${
            orphan.name
          }> is still open where </${name}> closes (line ${lineAt(
            source,
            index,
          )})`,
          at: orphan.index,
        });
      }
      stack.splice(target);
      continue;
    }

    if (VOID_TAGS.has(name) || /\/>\s*$/.test(text)) continue;
    stack.push({ name, index, conditionalDepth: depthAt(index) });
  }

  for (const open of stack) {
    if (OPTIONAL_END_TAGS.has(open.name)) continue;
    problems.push({
      code: LAYOUT_PROBLEM.UNCLOSED_TAG,
      severity: severityFor(open.conditionalDepth),
      detail: `Line ${lineAt(source, open.index)}: <${
        open.name
      }> is never closed`,
      at: open.index,
    });
  }

  return problems;
}

export interface ValidateLayoutOptions {
  /**
   * The fragment catalog, for a layout that `{% extends %}` another one: the
   * content slot it needs may be declared by its parent. Without the catalog the
   * slot check is skipped for such a layout rather than guessed at.
   */
  fragments?: Record<string, string>;
}

/**
 * Every problem in a layout source, worst first.
 *
 * Empty source returns nothing: a layout being created is empty for one render
 * before the form seeds it, and reporting "no content block" against a blank
 * editor is noise, not a finding.
 */
export function validateLayoutSource(
  source: string,
  options: ValidateLayoutOptions = {},
): LayoutProblem[] {
  if (!source.trim()) return [];

  const twig = scanTwig(source);
  const problems: LayoutProblem[] = [
    ...twig.problems,
    ...scanHtml(source, twig.masked, twig.conditionalDepthAt),
  ];

  const extendsMatch = source.match(/\{%-?\s*extends\s+['"]([^'"]+)['"]/);
  const parentKey = extendsMatch?.[1] ?? null;

  problems.push(...checkContentSlot(source, parentKey, options.fragments));
  if (!parentKey) problems.push(...checkDocumentShell(twig.masked));

  const rank = (problem: LayoutProblem) =>
    problem.severity === 'error' ? 0 : 1;
  return problems.sort((a, b) => rank(a) - rank(b));
}

/**
 * The layout has to have a hole in it. Blocks inherited from a parent count, so a
 * layout that only re-skins another one is not asked to redeclare the slot.
 */
function checkContentSlot(
  source: string,
  parentKey: string | null,
  fragments?: Record<string, string>,
): LayoutProblem[] {
  const own = layoutBlocks(source).filter((name) => !CHROME_BLOCKS.has(name));
  const inherited = parentKey
    ? layoutBlocks(fragments?.[parentKey] ?? '').filter(
        (name) => !CHROME_BLOCKS.has(name),
      )
    : [];

  // Extends an unknown parent: its blocks are unknowable, so claiming there is
  // no slot would be a guess. Reachability is warned about elsewhere.
  if (parentKey && !fragments?.[parentKey]) return [];

  const slots = [...own, ...inherited];
  if (slots.length === 0) {
    return [
      {
        code: LAYOUT_PROBLEM.NO_CONTENT_SLOT,
        severity: 'error',
        detail: `No {% block %} declared — add {% block ${CONVENTIONAL_SLOTS[0]} %}{% endblock %} where the email's content belongs`,
      },
    ];
  }

  if (!slots.some((name) => CONVENTIONAL_SLOTS.includes(name))) {
    return [
      {
        code: LAYOUT_PROBLEM.UNCONVENTIONAL_SLOT,
        severity: 'warning',
        detail: `Declares ${slots
          .map((name) => `"${name}"`)
          .join(', ')} but no "${CONVENTIONAL_SLOTS.join('"/"')}" block`,
      },
    ];
  }

  return [];
}

/** A layout that extends nothing is the whole document, so it has to be one. */
function checkDocumentShell(masked: string): LayoutProblem[] {
  const problems: LayoutProblem[] = [];
  // Checked against the masked source: `<body style="{{ … }}">` is still a
  // `<body>` even when its attributes are Twig.
  const has = (tag: string) => new RegExp(`<${tag}\\b`, 'i').test(masked);

  const missing = ['html', 'body'].filter((tag) => !has(tag));
  if (missing.length > 0) {
    problems.push({
      code: LAYOUT_PROBLEM.NOT_A_DOCUMENT,
      severity: 'error',
      detail: `A layout is the whole email document, but this has no ${missing
        .map((tag) => `<${tag}>`)
        .join(' and no ')}`,
    });
    // Without a shell at all, the doctype is the smaller half of the same news.
    return problems;
  }

  if (!/<!doctype/i.test(masked)) {
    problems.push({
      code: LAYOUT_PROBLEM.MISSING_DOCTYPE,
      severity: 'warning',
      detail: 'No <!doctype html> — several clients fall back to quirks mode',
    });
  }

  return problems;
}

export function layoutErrors(problems: LayoutProblem[]): LayoutProblem[] {
  return problems.filter((problem) => problem.severity === 'error');
}

export function layoutWarnings(problems: LayoutProblem[]): LayoutProblem[] {
  return problems.filter((problem) => problem.severity === 'warning');
}

/**
 * The errors that may block this save.
 *
 * A partner customizing a shipped layout, or editing one they inherited, must
 * not be made to fix damage they did not cause: an error the stored source
 * already had is reported but does not stand in the way of an unrelated change.
 * Anything the draft introduces does.
 *
 * `baseline` is empty for a layout being created — there is nothing to inherit,
 * so every error is the author's own.
 */
export function blockingLayoutErrors(
  draft: LayoutProblem[],
  baseline: LayoutProblem[],
): LayoutProblem[] {
  const preexisting = new Map<string, number>();
  for (const problem of layoutErrors(baseline)) {
    preexisting.set(problem.code, (preexisting.get(problem.code) ?? 0) + 1);
  }

  return layoutErrors(draft).filter((problem) => {
    const allowance = preexisting.get(problem.code) ?? 0;
    // Per occurrence, not per code: a layout that already had one unclosed tag
    // may keep it, but a second one is new damage.
    if (allowance === 0) return true;
    preexisting.set(problem.code, allowance - 1);
    return false;
  });
}

/**
 * The source a new layout starts from: a real email document — doctype,
 * resets-free minimal head, centred 600px content table — with the content block
 * already in place.
 *
 * A blank editor is how nonsense layouts get written: the gate would reject the
 * first save with three errors at once, which teaches the format by rejection.
 * Starting from something that already passes teaches it by example instead.
 */
export const LAYOUT_STARTER = `<!doctype html>
<html>
  <head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>{{ _subject }}</title>
  </head>
  <body style="margin:0;padding:0;background:{{ theme.colors.background|default('#F4F4F5') }};font-family:Helvetica, Arial, sans-serif;">
    {# The preview line clients show next to the subject. #}
    <div style="display:none;max-height:0;overflow:hidden;mso-hide:all;">
      {% block preheader %}{{ _pretext }}{% endblock %}
    </div>
    <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%">
      <tbody>
        <tr>
          <td align="center" style="padding:24px 0;">
            <table role="presentation" align="center" border="0" cellpadding="0" cellspacing="0" width="600"
                   style="max-width:600px;background:{{ theme.colors.surface|default('#FFFFFF') }};border-radius:4px;">
              <tbody>
                <tr>
                  <td style="padding:0;">
                    {# The email's own content is rendered here. #}
                    {% block content %}{% endblock %}
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  </body>
</html>
`;
